<template>
  <AppButton
    v-tooltip.bottom="rotulo"
    icon="pi pi-bell"
    class="p-button-text sino-btn"
    :class="{ 'sino-btn--tem': temNovidade }"
    :aria-label="rotulo"
    data-testid="topbar-chamados-sino"
    @click="alternarPainel"
  >
    <i class="pi pi-bell"></i>
    <span
      v-if="temNovidade"
      class="sino-badge"
      data-testid="topbar-chamados-badge"
    >{{ totalExibido }}</span>
  </AppButton>

  <!--
    O PAINEL. Popover da casa, ancorado no sino.
    O conteúdo é buscado ao ABRIR, nunca no polling — ver `buscarNovidades`.
  -->
  <Popover ref="painelRef" data-testid="topbar-chamados-painel">
    <div class="painel">
      <div class="painel-topo">
        <strong>{{ t('Notificações') }}</strong>
        <span v-if="total > 0" class="painel-total">{{ total }}</span>
      </div>

      <div v-if="carregandoPainel" class="painel-vazio">
        <i class="pi pi-spin pi-spinner"></i> {{ t('Carregando...') }}
      </div>

      <!-- Vazio é um ESTADO, não um painel em branco: sem esta linha a pessoa
           não sabe se não há nada ou se falhou. -->
      <div v-else-if="itens.length === 0" class="painel-vazio" data-testid="topbar-chamados-vazio">
        <i class="pi pi-check-circle"></i> {{ t('Nada novo por aqui') }}
      </div>

      <ul v-else class="painel-lista">
        <li
          v-for="item in itens"
          :key="`${item.origem}-${item.id}`"
          class="painel-item"
          :class="{ 'painel-item--alta': item.enfase === 'alta' }"
          :data-testid="`topbar-notificacao-${item.id}`"
          @click="irPara(item)"
        >
          <i class="painel-icone" :class="iconeDe(item.origem)"></i>
          <div class="painel-texto">
            <span class="painel-titulo">{{ item.titulo }}</span>
            <span class="painel-meta">
              {{ t(rotuloStatus(item.descricao)) }}
              <template v-if="item.quando"> · {{ quandoLegivel(item.quando) }}</template>
            </span>
          </div>
        </li>
      </ul>
    </div>
  </Popover>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import AppButton from '../ui/AppButton.vue';
import Popover from '../ui/Popover.vue';

import { eventBus } from '../eventBus';
import { log } from '../utils/DebugLogger';
import { useChamadosNaoLidos } from '../composables/useChamadosNaoLidos';
import { useDateFormatter } from '../composables/useDateFormatter';
import type { Chamados_NovidadeRow } from '../contracts/Chamados_NovidadeRow';

/**
 * O sino de chamados na barra de topo.
 *
 * ---------------------------------------------------------------------------
 * POR QUE SAIU DO BOTÃO FLUTUANTE
 * ---------------------------------------------------------------------------
 * O contador estava no FAB de suporte, que só aparece quando a pessoa procura
 * ajuda — ou seja, exatamente quando ela JÁ decidiu abrir chamado. Notificação
 * serve para o contrário: avisar quem NÃO está pensando no assunto. Escondida
 * num botão que se abre por hover, ela não avisa ninguém.
 *
 * A barra de topo é o único lugar que fica visível em todas as telas.
 *
 * ---------------------------------------------------------------------------
 * O POPUP SÓ DISPARA NO QUE É NOVIDADE DE VERDADE
 * ---------------------------------------------------------------------------
 * Três regras, e cada uma existe para evitar um jeito de o aviso virar ruído:
 *
 *  1. NUNCA na primeira leitura. A primeira consulta estabelece a linha de
 *     base. Sem isso, todo F5 traria "você tem 4 chamados" — e as pessoas
 *     aprendem em dois dias a fechar o popup sem ler.
 *
 *  2. Só quando o número SOBE. Cair é a pessoa tendo lido algo; anunciar isso
 *     seria avisar sobre o próprio trabalho dela.
 *
 *  3. O texto diz QUANTOS chegaram, não o total. "2 novos" e "6 no total" são
 *     informações diferentes, e a que importa no instante do aviso é a
 *     primeira.
 */

const { t } = useI18n();
const router = useRouter();
const { total, temNovidade, buscarNovidades } = useChamadosNaoLidos();
const { fromApiDateTimeFormat } = useDateFormatter();

const totalExibido = computed(() => (total.value > 9 ? '9+' : String(total.value)));

/*
 * O NÚMERO É CONCATENADO, não interpolado por `t()`.
 *
 * Medido nesta tela: `t('{n} chamados', { n: 3 })` devolveu a chave CRUA,
 * com o `{n}` literal, quando a chave não tem tradução registrada — que é o
 * estado da maioria das chaves novas. O usuário veria "{n} chamados com
 * novidade". É a mesma família do bug #53/#56 (vue-i18n comendo placeholder),
 * e aqui a saída é simples: traduzir só as PALAVRAS e juntar o número fora.
 */
const rotulo = computed(() =>
  temNovidade.value ? `${total.value} ${t('chamados com novidade')}` : t('Chamados'),
);

/**
 * O último total conhecido. `null` = ainda não houve leitura nenhuma, e é o
 * que distingue "abriu o sistema" de "chegou coisa nova".
 */
const anterior = ref<number | null>(null);

watch(total, (agora) => {
  const antes = anterior.value;
  anterior.value = agora;

  // Primeira leitura: só estabelece a base, não anuncia.
  if (antes === null) {
    return;
  }

  if (agora <= antes) {
    return;
  }

  const novos = agora - antes;

  log('ChamadosSino', 'watch:total', 'Novidade', { antes, agora, novos });

  eventBus.emit('globalMessage.show', {
    type: 'info',
    message:
      novos === 1
        ? t('Novo chamado com novidade')
        : `${novos} ${t('chamados com novidade')}`,
    // 6 s: tempo de ler sem virar obstáculo. Um aviso preso na tela vira
    // trabalho — a pessoa tem de fechá-lo para continuar.
    duration: 6000,
  });
});

const painelRef = ref<{ toggle: (e: Event) => void; hide: () => void } | null>(null);
const itens = ref<Chamados_NovidadeRow[]>([]);
const carregandoPainel = ref(false);

/**
 * Abre o painel e SÓ ENTÃO busca a lista.
 *
 * Buscar ao montar deixaria a lista velha na mão de quem abre uma hora
 * depois; buscar no timer custaria a lista inteira por minuto por usuário.
 * O clique é o único momento em que ela importa e é o único em que ela sai.
 */
const alternarPainel = async (evento: Event): Promise<void> => {
  painelRef.value?.toggle(evento);

  carregandoPainel.value = true;
  try {
    const { rows } = await buscarNovidades();
    itens.value = rows;
  } catch (e) {
    // Falha aqui NÃO some com o painel: uma lista vazia sem explicação faz a
    // pessoa concluir que não há nada, que é o oposto do que aconteceu.
    itens.value = [];
    eventBus.emit(
      'globalMessage.error',
      t('Não consegui carregar as notificações') + ': ' + (e instanceof Error ? e.message : String(e)),
    );
  } finally {
    carregandoPainel.value = false;
  }
};

const ROTULOS_STATUS: Record<string, string> = {
  aberto: 'Aberto',
  em_atendimento: 'Em atendimento',
  aguardando_solicitante: 'Aguardando o solicitante',
  resolvido: 'Resolvido',
};

/*
 * Os campos chegam OPCIONAIS: o gerador de contratos marca assim tudo que tem
 * default no PHP. Tratar `undefined` aqui é mais honesto que um `!` no
 * template escondendo que o campo pode faltar.
 */
const rotuloStatus = (s?: string): string => (s ? (ROTULOS_STATUS[s] ?? s) : '');

/** Ícone por ORIGEM — é o que deixa o painel receber outros avisos depois. */
const iconeDe = (origem?: string): string =>
  (origem ? ({ chamado: 'pi pi-comments' } as Record<string, string>)[origem] : undefined) ?? 'pi pi-bell';

/**
 * "há 5 min", e não a data completa.
 *
 * Num painel de notificação o que importa é a distância até agora; a data
 * exata está no chamado, a um clique.
 */
const quandoLegivel = (iso?: string): string => {
  if (!iso) return '';
  const d = fromApiDateTimeFormat(iso);
  if (!d) return '';

  const min = Math.round((Date.now() - d.getTime()) / 60000);
  if (min < 1) return t('agora');
  if (min < 60) return `${t('há')} ${min} min`;

  const h = Math.round(min / 60);
  if (h < 24) return `${t('há')} ${h} h`;

  return `${t('há')} ${Math.round(h / 24)} d`;
};

/*
  Um aviso de CHAMADO abre o próprio chamado, no modal global (ModalManager),
  em cima da tela em que a pessoa está — ir para a lista e obrigá-la a achar a
  linha era o oposto de "clique aqui". A `rota` do backend continua sendo o
  caminho para qualquer outra origem que o painel venha a receber.
*/
const irPara = (item: Chamados_NovidadeRow): void => {
  painelRef.value?.hide();
  if (item.origem === 'chamado' && typeof item.id === 'number') {
    log('ChamadosSino', 'irPara', 'Abrindo chamado no modal', item.id);
    eventBus.emit('modal.chamados.open', { modo: 'detalhe', id: item.id });
    return;
  }
  void router.push(String(item.rota ?? '/chamados'));
};
</script>

<style scoped>
.sino-btn {
  position: relative;
}

/*
  Mesmo padrão dos outros botões da TopBar (hambúrguer, menu do usuário): a
  barra tem fundo `--app-primary-color`, então o ícone usa
  `--app-secondary-color` (a cor de contraste do tenant) e o hover é branco
  translúcido. `primary outlined` aqui seria invisível — primário sobre primário.
  Sem essa regra o botão `text` herda a cor de texto do PrimeVue, que no tenant
  de testes é a cor exata do fundo.
*/
.sino-btn,
.sino-btn :deep(.pi-bell) {
  color: var(--app-secondary-color) !important;
}

.sino-btn:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

/*
  Com novidade o ícone ganha cor: o badge sozinho é pequeno demais para ser
  percebido em visão periférica.
*/
.sino-btn--tem :deep(.pi-bell),
.sino-btn--tem i {
  color: #d32f2f !important;
}

/* --- painel --- */
.painel {
  width: 340px;
  max-width: 92vw;
}

.painel-topo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 4px 8px;
  border-bottom: 1px solid #eceff3;
  color: #44505c;
}

.painel-total {
  font-size: 0.72rem;
  background: #eceff3;
  border-radius: 8px;
  padding: 1px 7px;
}

.painel-vazio {
  padding: 18px 6px;
  text-align: center;
  color: #7b858f;
  font-size: 0.85rem;
}

.painel-lista {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 340px;
  overflow-y: auto;
}

.painel-item {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 9px 6px;
  border-bottom: 1px solid #f2f5f8;
  cursor: pointer;
}

.painel-item:hover {
  background: #f7f9fc;
}

/* Ênfase por barra lateral, não por fundo colorido: o fundo brigaria com o
   hover e faria a lista inteira parecer alarme. */
.painel-item--alta {
  border-left: 3px solid #d32f2f;
  padding-left: 4px;
}

.painel-icone {
  color: #7b858f;
  margin-top: 2px;
}

.painel-texto {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.painel-titulo {
  font-size: 0.85rem;
  color: #263238;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.painel-meta {
  font-size: 0.72rem;
  color: #7b858f;
}

.sino-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #d32f2f;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  pointer-events: none;
}
</style>
